import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AppThunkAPIType } from "../../app/appTypes";
import { RootState } from "../../app/store";
import { getWithAuth } from "../../http";
import { logout } from "../user/userSlice";

// Package names as the backend's Package enum spells them (stored by name, append-only).
export type PackageName =
    | "CompanySupplierDictionary"
    | "Projects"
    | "BusinessCase"
    | "ProjectArchive"
    | "MeettingMinutes"
    | "URS"
    | "UrsTemplates"
    | "RiskAnalysis"


/** A company role's level for one package; ordered None < Read < ReadWrite. */
export type PackageAccess = "None" | "Read" | "ReadWrite"

export const packageAccessLevels: PackageAccess[] = ["None", "Read", "ReadWrite"]

export interface RolePermission {
    package: string
    access: PackageAccess
    /** Read-only: the module the package is shown under. */
    moduleCode?: string
    moduleName?: string
}

export interface MyPermissions {
    role: string | null
    companyRoleId: string | null
    companyRoleName: string | null
    bypassesMatrix: boolean
    permissions: Record<string, PackageAccess>
}

export const packageL10nKey: Record<string, string> = {
    CompanySupplierDictionary: "menu-supplier",
    Projects: "menu-projects-list",
    BusinessCase: "menu-business-cases",
    ProjectArchive: "menu-archived-projects-list",
    MeettingMinutes: "menu-meeting-minutes",
    URS: "menu-urs",
    UrsTemplates: "menu-urs-templates",
    RiskAnalysis: "risk-analysis",
}

export const packageLabel = (
    l10n: { getString: (id: string, args?: any, fallback?: string) => string },
    pkg: string
): string => {
    const key = packageL10nKey[pkg]
    return key ? l10n.getString(key, null, pkg) : pkg
}

interface PermissionsState {
    status: "idle" | "loading" | "loaded" | "error"
    userId: string | null
    data: MyPermissions | null
}

const initialState: PermissionsState = {
    status: "idle",
    userId: null,
    data: null,
}

export const loadMyPermissions = createAsyncThunk<MyPermissions, string, AppThunkAPIType>(
    "permissions/loadMine",
    async (_userId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: "api/me/permissions" }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as MyPermissions
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" })
        }
    }
)

export const permissionsSlice = createSlice({
    name: "permissions",
    initialState,
    reducers: {
        invalidateMyPermissions: () => initialState,
    },
    extraReducers: builder => {
        builder
            .addCase(loadMyPermissions.pending, (state, action) => {
                state.status = "loading"
                state.userId = action.meta.arg
            })
            .addCase(loadMyPermissions.fulfilled, (state, action) => {
                if (state.userId !== action.meta.arg) return
                state.status = "loaded"
                state.data = action.payload
            })
            .addCase(loadMyPermissions.rejected, (state, action) => {
                if (state.userId !== action.meta.arg) return
                state.status = "error"
                state.data = null
            })
        .addCase(logout.fulfilled, (state) => {
                state = initialState
            })
    },
})

export const { invalidateMyPermissions } = permissionsSlice.actions

export const selectPermissions = (state: RootState) => state.permissions

export default permissionsSlice.reducer
